/* Oxc note: The legacy flex layout and every `.rt-*` theme rule depend on div-based rows and cells, so table semantics are supplied by ARIA roles. */
/* oxlint-disable jsx-a11y/interactive-supports-focus -- Rows are not interactive; only sortable column headers take focus. */
/* oxlint-disable jsx-a11y/no-static-element-interactions -- The resize handle is a pointer-only affordance beside a keyboard-operable header. */
/* oxlint-disable react/no-array-index-key -- Padding rows are placeholders with no identity beyond their position. */
import { type CellData, type RowData, type TableFeatures, tableFeatures, useTable } from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";
import PropTypes from "prop-types";
import {
  type ComponentType,
  type CSSProperties,
  isValidElement,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/*
 * Owned replacement for the `react-table` 6.x component the application used
 * until now. It renders the same `.ReactTable` / `.rt-*` markup, so the theme
 * rules in `src/styles/theme/_base.scss`, the vendored base stylesheet in
 * `src/styles/vendor/data-table.css`, and the checked-in visual baselines all
 * keep working.
 *
 * `@tanstack/react-table` supplies the column model, the core row model, and
 * cell value resolution. Two behaviors are owned here instead of being handed
 * to TanStack, because the application depends on the legacy semantics and v8
 * resolves them differently:
 *
 *   - Sorting. v8 defaults to descending-first for numeric columns and clears
 *     the sort on the third click. The legacy contract is ascending-first for
 *     every column, descending second, ascending again on a third plain click,
 *     removal only on a third shift-click, and ties broken by the original row
 *     index (reversed while the primary sort is descending). `orderByMethod`
 *     also lets a caller reorder the whole array after sorting, which has no
 *     v8 equivalent.
 *   - Column widths. The legacy layout is flex based (`minWidth` seeds the flex
 *     basis, `maxWidth` caps it) rather than v8's fixed `size` model, and a
 *     resize drag must not also trigger a sort.
 *
 * Only the props the application actually passes are supported. Pagination and
 * column filtering are deliberately absent: every legacy call site disabled
 * both.
 */

const DEFAULT_MIN_WIDTH = 100;
const MIN_RESIZE_WIDTH = 11;
const INDEX_KEY = "_index";
const ORIGINAL_KEY = "_original";

type SourceRow = Record<string, unknown>;

export type DecoratedRow = SourceRow & {
  [INDEX_KEY]: number;
  [ORIGINAL_KEY]: SourceRow;
};

export type CellInfo = {
  column: DataTableColumn;
  index: number;
  original: SourceRow;
  pageSize: number;
  row: DecoratedRow;
  value: unknown;
  viewIndex: number;
};

export type DataTableColumn = {
  Cell?: ComponentType<CellInfo> | ReactNode;
  Header?: ComponentType<{ column: DataTableColumn; data: DecoratedRow[] }> | ReactNode;
  accessor?: string;
  className?: string;
  defaultSortDesc?: boolean;
  headerClassName?: string;
  headerStyle?: CSSProperties;
  id?: string;
  maxWidth?: number;
  minWidth?: number;
  sortable?: boolean;
  style?: CSSProperties;
  width?: number;
};

type ResolvedColumn = DataTableColumn & { id: string };

const tableFeatureSet = tableFeatures({});

declare module "@tanstack/react-table" {
  // The legacy column definition rides along on the TanStack column so headers
  // and cells can read `Header`, `Cell`, and the width contract back out.
  // Declaration merging has to repeat TanStack's own type parameters, which in
  // v9 lead with TFeatures.
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData,
  > {
    column: ResolvedColumn;
  }
}

type SortEntry = { desc: boolean; id: string };
type ResizedColumn = { id: string; value: number };
type RowComparator = (a: DecoratedRow, b: DecoratedRow) => number;

export type OrderByMethod = (
  rows: DecoratedRow[],
  comparators: RowComparator[],
  ascending: boolean[],
  indexKey: typeof INDEX_KEY,
) => DecoratedRow[];

export type DataTableProps = {
  className?: string;
  columns: DataTableColumn[];
  data: SourceRow[];
  defaultSortDesc?: boolean;
  /** Stable identity for a source row when table sorting or data updates can reorder it. */
  getRowId?: (row: SourceRow, index: number) => string;
  minRows?: number;
  multiSort?: boolean;
  noDataText?: ReactNode;
  orderByMethod?: OrderByMethod;
  pageSize?: number;
  resizable?: boolean;
  sortable?: boolean;
  style?: CSSProperties;
};

// Matches react-table 6's `defaultSortMethod`: nulls compare as empty strings
// and string comparison is case-insensitive. Values are compared with the same
// loose relational operators the legacy comparator used, so mixed types keep
// ordering exactly as they did.
function defaultSortMethod(a: unknown, b: unknown): number {
  // oxlint-disable-next-line typescript/no-explicit-any -- Relational comparison across the heterogeneous cell values is the legacy contract.
  const left = (a === null || a === undefined ? "" : a) as any;
  // oxlint-disable-next-line typescript/no-explicit-any -- Relational comparison across the heterogeneous cell values is the legacy contract.
  const right = (b === null || b === undefined ? "" : b) as any;
  const first = typeof left === "string" ? left.toLowerCase() : left;
  const second = typeof right === "string" ? right.toLowerCase() : right;
  if (first > second) {
    return 1;
  }
  if (first < second) {
    return -1;
  }
  return 0;
}

// Matches react-table 6's `orderBy`: the original row index breaks ties, and
// the tie-break direction follows the primary sort.
const orderBy: OrderByMethod = (rows, comparators, ascending, indexKey) =>
  rows.sort((rowA, rowB) => {
    for (let i = 0; i < comparators.length; i += 1) {
      const comparison = comparators[i](rowA, rowB);
      if (comparison) {
        return ascending[i] ? comparison : -comparison;
      }
    }
    return ascending[0] ? rowA[indexKey] - rowB[indexKey] : rowB[indexKey] - rowA[indexKey];
  });

function resolveColumnId(column: DataTableColumn, index: number): string {
  if (column.id !== undefined) {
    return column.id;
  }
  if (typeof column.accessor === "string") {
    return column.accessor;
  }
  return `_column_${index}`;
}

// react-table 6 rendered `Cell` and `Header` through `normalizeComponent`,
// which turns a function into a component rather than calling it inline. Cell
// renderers therefore mount as their own components, and call sites that build
// a renderer per render intentionally remount their cells.
function renderNode<Props extends object>(renderer: unknown, props: Props, fallback?: ReactNode): ReactNode {
  if (isValidElement(renderer) || typeof renderer === "string") {
    return renderer;
  }
  if (typeof renderer === "function") {
    const Renderer = renderer as ComponentType<Props>;
    return <Renderer {...props} />;
  }
  return fallback;
}

// The legacy sort arrows were Glyphicons Halflings pseudo-elements. Bootstrap 5
// dropped that font, so they rendered as empty boxes. Draw them with Lucide
// instead, matching how the accordion chevrons were replaced.
function SortIndicator({ sort }: { sort: SortEntry | undefined }) {
  if (!sort) {
    return <ChevronsUpDown aria-hidden="true" className="rt-sort-indicator" size={14} />;
  }
  const Icon = sort.desc ? ChevronDown : ChevronUp;
  return <Icon aria-hidden="true" className="rt-sort-indicator -active" size={14} />;
}

function ariaSort(sort: SortEntry | undefined): "ascending" | "descending" | "none" {
  if (!sort) {
    return "none";
  }
  return sort.desc ? "descending" : "ascending";
}

function pageXOf(event: globalThis.MouseEvent | globalThis.TouchEvent): number {
  return "changedTouches" in event ? event.changedTouches[0].pageX : event.pageX;
}

// react-table 6 resolved a dotted accessor as a path. This adapter reads a plain key, so a dotted
// accessor renders blanks. Most call sites are untyped .jsx files (`checkJs` is off), so the
// TypeScript props do not protect them; this is the runtime guard that does.
function assertResolvableAccessors(columns: DataTableProps["columns"]) {
  for (const column of columns) {
    const { accessor } = column;
    if (accessor !== undefined && typeof accessor !== "string") {
      console.error("`accessor` in `DataTable` must be a string.");
      continue;
    }
    if (typeof accessor === "string" && accessor.includes(".")) {
      console.error(
        `\`accessor\` in \`DataTable\` is a dotted path ("${accessor}"), which this table does not resolve. ` +
          "Flatten the value before passing it as data, or give the column an explicit id and accessor.",
      );
    }
  }
}

function accessorValidationSignature(columns: DataTableProps["columns"]): string {
  return columns
    .map(({ accessor }) => (typeof accessor === "string" ? `string:${accessor}` : typeof accessor))
    .join("\u0000");
}

export default function DataTable({
  className,
  columns,
  data,
  defaultSortDesc = false,
  getRowId,
  minRows,
  multiSort = true,
  noDataText = "No rows found",
  orderByMethod,
  pageSize,
  resizable = true,
  sortable = true,
  style,
}: DataTableProps) {
  const [sorted, setSorted] = useState<SortEntry[]>([]);
  const [resized, setResized] = useState<ResizedColumn[]>([]);
  const [currentlyResizing, setCurrentlyResizing] = useState<string | null>(null);
  const resizingRef = useRef<{ id: string; parentWidth: number; startX: number } | null>(null);
  const skipNextSort = useRef(false);
  const validatedAccessorSignature = useRef<string | undefined>(undefined);

  // React 19 no longer validates `propTypes`, so the accessor guard below cannot live there. It ran
  // as a custom PropTypes validator until the React 19 pin silently disabled it, which is exactly the
  // failure a dotted accessor produces: blank cells and no diagnostic. Validate synchronously when
  // the accessor surface changes, but not again for sorting/resizing state renders that never
  // recreate the parent element under the old PropTypes path.
  const validationSignature = accessorValidationSignature(columns);
  if (process.env.NODE_ENV !== "production" && validatedAccessorSignature.current !== validationSignature) {
    validatedAccessorSignature.current = validationSignature;
    assertResolvableAccessors(columns);
  }

  const resolvedColumns = useMemo<ResolvedColumn[]>(
    () => columns.map((column, index) => ({ ...column, id: resolveColumnId(column, index) })),
    [columns],
  );

  // Legacy rows carry their original index and a reference to the source
  // object, so a cell can edit the correct record after sorting. Values are
  // read through the accessor but stored under the column id, which is how
  // react-table 6 resolved a column whose id differed from its accessor.
  const decoratedRows = useMemo<DecoratedRow[]>(
    () =>
      data.map((row, index) => {
        const decorated: DecoratedRow = { [ORIGINAL_KEY]: row, [INDEX_KEY]: index };
        for (const column of resolvedColumns) {
          decorated[column.id] = row[column.accessor ?? column.id];
        }
        return decorated;
      }),
    [data, resolvedColumns],
  );

  const sortedRows = useMemo(() => {
    if (!sorted.length) {
      return decoratedRows;
    }
    const comparators = sorted.map<RowComparator>((sort) => (a, b) => defaultSortMethod(a[sort.id], b[sort.id]));
    const ascending = sorted.map((sort) => !sort.desc);
    const sort = orderByMethod ?? orderBy;
    return sort([...decoratedRows], comparators, ascending, INDEX_KEY);
  }, [decoratedRows, orderByMethod, sorted]);

  const resolvedPageSize = pageSize === undefined ? sortedRows.length : pageSize;
  const pageRows = useMemo(() => sortedRows.slice(0, resolvedPageSize), [resolvedPageSize, sortedRows]);
  const resolvedMinRows = minRows === undefined ? resolvedPageSize : minRows;
  const padRowCount = Math.max(resolvedMinRows - pageRows.length, 0);

  const tableColumns = useMemo(
    () =>
      resolvedColumns.map((column) => ({
        accessorFn: (row: DecoratedRow) => row[column.id],
        id: column.id,
        meta: { column },
      })),
    [resolvedColumns],
  );

  const table = useTable<typeof tableFeatureSet, DecoratedRow>({
    features: tableFeatureSet,
    columns: tableColumns,
    data: pageRows,
    getRowId: getRowId
      ? (row: DecoratedRow, index: number) => getRowId(row[ORIGINAL_KEY], row[INDEX_KEY] ?? index)
      : undefined,
  });

  const widthOf = useCallback(
    (column: ResolvedColumn) => {
      const override = resized.find((entry) => entry.id === column.id);
      if (override !== undefined) {
        return override.value;
      }
      if (column.width !== undefined) {
        return column.width;
      }
      return column.minWidth === undefined ? DEFAULT_MIN_WIDTH : column.minWidth;
    },
    [resized],
  );

  const maxWidthOf = useCallback(
    (column: ResolvedColumn) => {
      const override = resized.find((entry) => entry.id === column.id);
      if (override !== undefined) {
        return override.value;
      }
      return column.width === undefined ? column.maxWidth : column.width;
    },
    [resized],
  );

  const cellStyle = useCallback(
    (column: ResolvedColumn): CSSProperties => {
      const width = widthOf(column);
      const maxWidth = maxWidthOf(column);
      return {
        flex: `${width} 0 auto`,
        maxWidth: maxWidth === undefined ? undefined : `${maxWidth}px`,
        width: `${width}px`,
      };
    },
    [maxWidthOf, widthOf],
  );

  const rowMinWidth = useMemo(
    () => resolvedColumns.reduce((total, column) => total + widthOf(column), 0),
    [resolvedColumns, widthOf],
  );

  const isSortable = useCallback(
    (column: ResolvedColumn) => (column.sortable === undefined ? sortable : column.sortable),
    [sortable],
  );

  const toggleSort = useCallback(
    (column: ResolvedColumn, additive: boolean, fromPointer = true) => {
      // A mouse drag ends with a click on the header, and that click must not
      // sort. Only the pointer path is swallowed: a keyboard activation is a
      // separate deliberate action, so it clears the guard and still sorts.
      if (skipNextSort.current) {
        skipNextSort.current = false;
        if (fromPointer) {
          return;
        }
      }
      const firstDirection = column.defaultSortDesc === undefined ? defaultSortDesc : column.defaultSortDesc;
      const secondDirection = !firstDirection;
      setSorted((current) => {
        const next = current.map((entry) => ({ ...entry }));
        const existingIndex = next.findIndex((entry) => entry.id === column.id);
        if (existingIndex === -1) {
          const added = { desc: firstDirection, id: column.id };
          return additive ? [...next, added] : [added];
        }
        const existing = next[existingIndex];
        if (existing.desc === secondDirection) {
          if (additive) {
            next.splice(existingIndex, 1);
            return next;
          }
          existing.desc = firstDirection;
          return [existing];
        }
        existing.desc = secondDirection;
        return additive ? next : [existing];
      });
    },
    [defaultSortDesc],
  );

  const stopResizing = useRef(() => {});

  const handleResizeMove = useCallback((event: globalThis.MouseEvent | globalThis.TouchEvent) => {
    event.stopPropagation();
    const resizing = resizingRef.current;
    if (!resizing) {
      return;
    }
    const width = Math.max(resizing.parentWidth + pageXOf(event) - resizing.startX, MIN_RESIZE_WIDTH);
    setResized((current) => [
      ...current.filter((entry) => entry.id !== resizing.id),
      { id: resizing.id, value: width },
    ]);
  }, []);

  const handleResizeEnd = useCallback((event: globalThis.MouseEvent | globalThis.TouchEvent) => {
    event.stopPropagation();
    const isTouch = event.type === "touchend" || event.type === "touchcancel";
    stopResizing.current();
    // A mouse drag ends with a click on the header, which would otherwise sort
    // the column that was just resized.
    if (!isTouch) {
      skipNextSort.current = true;
    }
  }, []);

  useEffect(() => {
    stopResizing.current = () => {
      document.removeEventListener("touchmove", handleResizeMove);
      document.removeEventListener("touchcancel", handleResizeEnd);
      document.removeEventListener("touchend", handleResizeEnd);
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
      document.removeEventListener("mouseleave", handleResizeEnd);
      resizingRef.current = null;
      setCurrentlyResizing(null);
    };
    // Unlike react-table 6, unmounting mid-drag detaches the document listeners.
    return () => stopResizing.current();
  }, [handleResizeEnd, handleResizeMove]);

  const handleResizeStart = useCallback(
    (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>, column: ResolvedColumn, isTouch: boolean) => {
      event.stopPropagation();
      const handle = event.target as HTMLElement;
      const parentWidth = handle.parentElement?.getBoundingClientRect().width ?? 0;
      resizingRef.current = { id: column.id, parentWidth, startX: pageXOf(event.nativeEvent) };
      setCurrentlyResizing(column.id);
      if (isTouch) {
        document.addEventListener("touchmove", handleResizeMove);
        document.addEventListener("touchcancel", handleResizeEnd);
        document.addEventListener("touchend", handleResizeEnd);
        return;
      }
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      document.addEventListener("mouseleave", handleResizeEnd);
    },
    [handleResizeEnd, handleResizeMove],
  );

  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  return (
    <div className={["ReactTable", className].filter(Boolean).join(" ")} style={style}>
      <div className={["rt-table", currentlyResizing ? "rt-resizing" : ""].filter(Boolean).join(" ")} role="table">
        <div className="rt-thead -header" role="rowgroup" style={{ minWidth: `${rowMinWidth}px` }}>
          <div className="rt-tr" role="row">
            {headers.map((header) => {
              const column = header.column.columnDef.meta?.column as ResolvedColumn;
              const sort = sorted.find((entry) => entry.id === column.id);
              const columnIsSortable = isSortable(column);
              const sortClass = sort ? (sort.desc ? "-sort-desc" : "-sort-asc") : "";
              return (
                <div
                  aria-sort={columnIsSortable ? ariaSort(sort) : undefined}
                  className={[
                    "rt-th",
                    column.headerClassName,
                    resizable ? "rt-resizable-header" : "",
                    sortClass,
                    columnIsSortable ? "-cursor-pointer" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={column.id}
                  onClick={
                    columnIsSortable
                      ? (event: MouseEvent<HTMLDivElement>) => toggleSort(column, multiSort && event.shiftKey)
                      : undefined
                  }
                  onKeyDown={
                    columnIsSortable
                      ? (event: KeyboardEvent<HTMLDivElement>) => {
                          if (event.key !== "Enter" && event.key !== " ") {
                            return;
                          }
                          // Space would otherwise scroll the page.
                          event.preventDefault();
                          toggleSort(column, multiSort && event.shiftKey, false);
                        }
                      : undefined
                  }
                  role="columnheader"
                  style={{ ...column.headerStyle, ...cellStyle(column) }}
                  tabIndex={columnIsSortable ? 0 : -1}
                >
                  <div className={resizable ? "rt-resizable-header-content" : undefined}>
                    {renderNode(column.Header, { column, data: sortedRows })}
                  </div>
                  {columnIsSortable ? <SortIndicator sort={sort} /> : null}
                  {resizable ? (
                    <div
                      className="rt-resizer"
                      onMouseDown={(event) => handleResizeStart(event, column, false)}
                      onTouchStart={(event) => handleResizeStart(event, column, true)}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className="rt-tbody" role="rowgroup" style={{ minWidth: `${rowMinWidth}px` }}>
          {table.getRowModel().rows.map((row, rowIndex) => (
            <div className="rt-tr-group" key={row.id} role="presentation">
              <div className={`rt-tr ${rowIndex % 2 ? "-even" : "-odd"}`} role="row">
                {row.getAllCells().map((cell) => {
                  const column = cell.column.columnDef.meta?.column as ResolvedColumn;
                  const value = cell.getValue();
                  const cellInfo: CellInfo = {
                    column,
                    index: row.original[INDEX_KEY],
                    original: row.original[ORIGINAL_KEY],
                    pageSize: resolvedPageSize,
                    row: row.original,
                    value,
                    viewIndex: rowIndex,
                  };
                  return (
                    <div
                      className={["rt-td", column.className].filter(Boolean).join(" ")}
                      key={column.id}
                      role="cell"
                      style={{ ...column.style, ...cellStyle(column) }}
                    >
                      {renderNode(column.Cell, cellInfo, value as ReactNode)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {Array.from({ length: padRowCount }, (_unused, padIndex) => (
            <div className="rt-tr-group" key={`pad-${padIndex}`} role="presentation">
              <div className={`rt-tr -padRow ${(pageRows.length + padIndex) % 2 ? "-even" : "-odd"}`} role="row">
                {resolvedColumns.map((column) => (
                  <div className="rt-td" key={column.id} role="cell" style={cellStyle(column)}>
                    <span>&nbsp;</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {pageRows.length ? null : <div className="rt-noData">{noDataText}</div>}
    </div>
  );
}

// Most call sites are untyped .jsx files (`checkJs` is off), so the TypeScript
// props above do not protect them; keep runtime validation for those callers.
DataTable.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      // The accessor guard lives in `assertResolvableAccessors`: React 19 ignores
      // `propTypes` entirely, so a custom validator here would never run.
      accessor: PropTypes.string,
      className: PropTypes.string,
      defaultSortDesc: PropTypes.bool,
      headerClassName: PropTypes.string,
      headerStyle: PropTypes.object,
      id: PropTypes.string,
      maxWidth: PropTypes.number,
      minWidth: PropTypes.number,
      sortable: PropTypes.bool,
      style: PropTypes.object,
      width: PropTypes.number,
    }),
  ).isRequired,
  data: PropTypes.array.isRequired,
  defaultSortDesc: PropTypes.bool,
  getRowId: PropTypes.func,
  minRows: PropTypes.number,
  multiSort: PropTypes.bool,
  noDataText: PropTypes.node,
  orderByMethod: PropTypes.func,
  pageSize: PropTypes.number,
  resizable: PropTypes.bool,
  sortable: PropTypes.bool,
  style: PropTypes.object,
};
